<template>
    <x-container fluid app-width>
        <x-layout>
            <x-flex v-if="pageLoading">
                <loading-spinner />
            </x-flex>

            <x-flex v-else>
                <submission-response
                    :submission="submission"
                    :offer="offer"
                    :is-editing="isEditingOffer"
                    :class="{ show: showResponsePanel || isEditingOffer }"
                    @closeResponsePanel="closeResponsePanel"
                />

                <x-container fluid class="summary-container" :class="{ 'response-active': showResponsePanel || isEditingOffer }">
                    <x-layout>
                        <x-flex xs12>
                            <summary-tabs-buttons
                                v-model="activeTab"
                                :tabs="summaryTabs"
                            />
                        </x-flex>
                    </x-layout>

                    <x-layout v-if="showReviewOfferBanner" mb-4>
                        <x-flex xs12>
                            Details have been edited since it was originally submitted. <a href="#" @click="showSubmissionChangesDialog = true">Click here to review changes</a>.
                        </x-flex>
                    </x-layout>

                    <x-layout v-if="showReviewPolicyWordingAndMRCBanner" mb-4>
                        <x-flex>
                            <view-details-banner
                                text="Review the draft Market Reform Contract and Policy Wording."
                                scroll-to-id="page-content"
                                @clicked="showDocumentsTab"
                            />
                        </x-flex>
                    </x-layout>

                    <template v-if="activeTab === 'product'">
                        <x-layout wrap>
                            <x-flex
                                xs6
                                class="left"
                                :class="{ 'mb-5': showSubmissionResponse }"
                            >
                                <x-card class="panel">
                                    <x-card-title>
                                        <h3>Submission information</h3>
                                    </x-card-title>

                                    <x-card-text>
                                        <standalone-policy-summary-data-list v-if="isStandalonePolicyCheck(submission)" v-model="submission" />

                                        <summary-data-list v-else v-model="submission"/>
                                    </x-card-text>
                                </x-card>

                                <x-card v-if="isCaptiveReinsurance" class="panel mt-5">
                                    <x-card-title>
                                        <h3>Reinsurer declaration details</h3>
                                    </x-card-title>

                                    <x-card-text>
                                        <summary-data-list v-model="declaration" :is-reinsurer="true" />
                                    </x-card-text>
                                </x-card>
                            </x-flex>

                            <x-flex
                                xs6
                                class="right"
                            >
                                <x-card v-if="offer.status === 'offered' && !isEditingOffer" class="panel mb-5">
                                    <x-card-title>
                                        <h3>Offer</h3>
                                    </x-card-title>

                                    <x-card-text>
                                        <offers-table
                                            :offers="[offer]"
                                            :submission="submission"
                                            :editable="insurerCanEdit"
                                            @edit="editOffer"
                                        />
                                    </x-card-text>
                                </x-card>

                                <x-card v-if="offer.status === 'declined' && !isEditingOffer" class="panel mb-5">
                                    <x-card-title>
                                        <h3>Offer declined</h3>
                                    </x-card-title>

                                    <x-card-text>
                                        <p>Reason: {{ offer.decline_reason }}</p>

                                        <x-btn color="tertiary" :disabled="!insurerCanEdit" @click="editOffer()">
                                            Edit Response
                                        </x-btn>
                                    </x-card-text>
                                </x-card>

                                <x-card v-if="hasObligor" class="panel mb-5">
                                    <x-card-title>
                                        <h3>Obligors</h3>
                                    </x-card-title>

                                    <x-card-text>
                                        <obligor-data-list v-model="submission.obligors" obligor :currency-code="submission.currency.code" />
                                    </x-card-text>
                                </x-card>

                                <x-card v-if="hasObligor && submission.guarantors.length > 0" class="panel mb-5">
                                    <x-card-title>
                                        <h3>Guarantors</h3>
                                    </x-card-title>

                                    <x-card-text>
                                        <obligor-data-list v-model="submission.guarantors" />
                                    </x-card-text>
                                </x-card>

                                <x-card v-if="showSubjectivities" class="panel mb-5">
                                    <x-card-title>
                                        <h3>Subjectivities</h3>
                                    </x-card-title>

                                    <x-card-text>
                                        <ul class="subjectivities-list">
                                            <li v-for="(subjectivity, index) in offer.subjectivities" :key="`subjectivity-${index}`">
                                                {{ subjectivity.name }}
                                            </li>
                                        </ul>
                                    </x-card-text>
                                </x-card>

                                <x-card v-if="isFirmOrder" class="panel mb-5">
                                    <x-card-title>
                                        <h3>Firm order</h3>
                                    </x-card-title>

                                    <x-card-text>
                                        <firm-order-summary :submission="submission" />
                                    </x-card-text>
                                </x-card>

                                <x-card class="panel">
                                    <x-card-title>
                                        <h3>Notes</h3>
                                    </x-card-title>

                                    <x-card-text>
                                        <submission-note :submissionId="submission.id" ref="note" />
                                    </x-card-text>
                                </x-card>
                            </x-flex>
                        </x-layout>
                    </template>

                    <template v-if="activeTab === 'additional_information'">
                        <x-layout>
                            <x-flex>
                                <additional-information-tab
                                    v-model="submission.information_sections"
                                    insurer
                                />
                            </x-flex>
                        </x-layout>
                    </template>

                    <template v-if="activeTab === 'documents'">
                        <document-list :is-standalone-policy="submission.risk_structure === 'standalone_policy'" :documentable-id="submission.id" documentable-type="submission" :can-delete="() => false"/>
                    </template>
                </x-container>
            </x-flex>
        </x-layout>

        <x-dialog v-if="isFirmOrder" v-model="showFirmOrderConfirmDialog" persistent max-width="512px" content-class="overflow-scroll-fit">
            <x-form ref="acceptForm" @submit.prevent="acceptFirmOrder">
                <x-container fluid>
                    <x-layout>
                        <x-flex xs12>
                            <h2>Accept firm order</h2>
                        </x-flex>
                    </x-layout>

                    <x-layout>
                        <x-flex xs12>
                            <p>
                                You are about to confirm the order of
                                <strong>{{ myTotalWrittenLineAmount | currency(submission.currency.code) }}</strong>
                                for
                                <strong>{{ submission.obligor_name }}</strong>.
                            </p>
                        </x-flex>
                    </x-layout>

                    <x-layout v-for="line in myReviewingLines" :key="line.id" mt-3>
                        <x-flex xs12>
                            <p
                                v-if="showSecurityName(line)"
                                class="security-name"
                            >
                                {{ securityName(line) }}
                            </p>
                            <x-text-field
                                v-model="underwriterReferences[line.id]"
                                label="Underwriter reference"
                                placeholder=" "
                                :rules="[v => ruleRequired(v, 'Underwriter reference')]"
                                outline
                            />
                        </x-flex>
                    </x-layout>

                    <x-layout>
                        <x-flex xs12>
                            <check-list
                                v-if="subjectivityCheckList.length > 0"
                                v-model="subjectivityCheckList"
                                label="Subjectivities"
                                :rules="[allSubjectivitiesSelected]"
                                item-hide-details
                                hide-addition
                            />
                        </x-flex>
                    </x-layout>

                    <x-layout class="response-actions" justify-space-between>
                        <x-flex xs6 class="text-xs-left">
                            <x-btn
                                class="ma-0"
                                color="tertiary"
                                small
                                @click="showFirmOrderConfirmDialog = false"
                            >
                                Cancel
                            </x-btn>
                        </x-flex>

                        <x-flex xs6 class="text-xs-right">
                            <x-btn
                                class="ma-0"
                                color="primary"
                                small
                                type="submit"
                                :loading="acceptFirmOrderLoading"
                            >
                                Accept
                            </x-btn>
                        </x-flex>
                    </x-layout>
                </x-container>
            </x-form>
        </x-dialog>

        <modal
            v-if="isFirmOrder"
            ref="decline"
            title="Decline order"
            confirmation
        >
            <p>
                You are about to decline the order of
                <strong>{{ myTotalWrittenLineAmount | currency(submission.currency.code) }}</strong> for
                <strong>{{ submission.obligor_name }}</strong>.
            </p>
            <p>
                Are you sure?
            </p>
        </modal>

        <modal
            v-if="allOffersAccepted"
            ref="approveFirmOrderModal"
            title="Approve firm order"
            confirmation
        >
            <p>
                You are about to approve the firm order.
            </p>
            <p>
                Are you sure?
            </p>
        </modal>

        <x-dialog v-if="showReviewOfferBanner" v-model="showSubmissionChangesDialog" persistent width="900">
            <x-container fluid grid-list-lg>
                <submission-changes-list :submission="submission">
                    <x-layout class="response-actions">
                        <x-flex class="text-xs-center">
                            <x-btn
                                class="ma-0"
                                color="primary"
                                small
                                @click="showSubmissionChangesDialog = false"
                            >
                                Close
                            </x-btn>
                        </x-flex>
                    </x-layout>
                </submission-changes-list>
            </x-container>
        </x-dialog>
    </x-container>
</template>

<script>
    import SummaryTabsButtons from '~/components/Shared/UIComponents/SummaryTabsButtons';
    import SummaryDataList from '~/components/Shared/UIComponents/SummaryDataList';
    import AdditionalInformationTab from '~/components/Shared/UIComponents/AdditionalInformationTab';
    import StatusChip from '~/components/Shared/UIComponents/StatusChip';
    import SubmissionResponse from '~/components/Insurer/SubmissionResponse';
    import SubmissionNote from '~/components/Insurer/SubmissionNote';
    import FirmOrderSummary from '~/components/Insurer/FirmOrderSummary';
    import OffersTable from '~/components/Insurer/OffersTable'
    import DocumentList from '~/components/Shared/UIComponents/DocumentList.vue';
    import Modal from '~/components/Shared/UIComponents/Modal';
    import CheckList from '~/components/Shared/UIComponents/CheckList';
    import rules from '~/mixins/rules';
    import ObligorDataList from "~/components/Shared/UIComponents/ObligorDataList";
    import SubmissionChangesList from "~/components/Insurer/UIComponents/SubmissionChangesList";
    import submissionHelpers from "../../../mixins/submissionHelpers";
    import ViewDetailsBanner from "../../../components/Shared/UIComponents/ViewDetailsBanner.vue";
    import StandalonePolicySummaryDataList from '../../../components/Shared/UIComponents/StandalonePolicySummaryDataList.vue';


    export default {
        name: 'InsurerSubmissionPage',
        components: {
            SummaryTabsButtons,
            ViewDetailsBanner,
            SummaryDataList,
            AdditionalInformationTab,
            SubmissionNote,
            StatusChip,
            SubmissionResponse,
            FirmOrderSummary,
            DocumentList,
            OffersTable,
            Modal,
            CheckList,
            ObligorDataList,
            SubmissionChangesList,
            StandalonePolicySummaryDataList
        },
        mixins: [
            rules,
            submissionHelpers
        ],
        props: ['showResponsePanel'],
        data() {
            return {
                activeTab: 'product',
                openSubmissionInformationPanelIndex: 0,
                openFirmOrderPanelIndex: 0,
                openDocumentsPanelIndex: 1,
                downloadingSlip: false,
                downloadingMRC: false,
                pageLoading: true,
                showFirmOrderConfirmDialog: false,
                acceptFirmOrderLoading: false,
                underwriterReferences: {},
                subjectivityCheckList: [],
                submission: { // Empty Submission model
                    id: -1,
                    insured: {
                        address: {}
                    },
                    product_category: {},
                    broker: {},
                    producing_broker: {
                        company: {}
                    },
                    main_product: {},
                    offers: [{}],
                    currency: {
                        code: 'USD'
                    },
                    obligator: {}
                },
                showSubmissionChangesDialog: false,
            }
        },
        created() {
            this.loadSubmission(this.$route.params.id);

            this.$eventBus.$on('insurerSubmissionChanged', async () => {
                await this.loadSubmission(this.$route.params.id);
            });

            this.$eventBus.$on('action-draft-declaration', this.downloadDraft);
            this.$eventBus.$on('action-draft-captive-declaration', () => { this.downloadCaptiveDraft() });
            this.$eventBus.$on('action-draft-reinsurer-declaration', () => { this.downloadReinsurerDraft() });
            this.$eventBus.$on('action-decline-firm-order', () => { this.declineFirmOrder() });
            this.$eventBus.$on('action-accept-firm-order', () => {
                this.initialiseUnderwriterReferences();
                this.showFirmOrderConfirmDialog = true;
            });
            this.$eventBus.$on('action-approve-firm-order', () => { this.approveFirmOrder() });
        },
        beforeDestroy() {
            this.$eventBus.$off('action-draft-declaration', this.downloadDraft);
            this.$eventBus.$off('action-draft-captive-declaration');
            this.$eventBus.$off('action-draft-reinsurer-declaration');
            this.$eventBus.$off('action-decline-firm-order');
            this.$eventBus.$off('action-accept-firm-order');
            this.$eventBus.$off('action-approve-firm-order');
        },
        methods: {
            showDocumentsTab() {
                this.activeTab = 'documents';
            },
            loadSubmission(submissionId) {
                this.pageLoading = true;

                axios.get(this.$root.api(`insurer/submission/${submissionId}`)).then(res => {
                    this.submission = res.data.data;

                    this.$emit('updateRiskStatus', this.submission.status);

                    this.subjectivityCheckList = this.submission.offers[0].subjectivities.map(s => {
                        return {
                            value: s.name,
                            editable: false,
                            selected: false
                        }
                    });

                    this.pageLoading = false;
                });
            },
            async downloadSlip(submissionId) {
                this.downloadingSlip = true;
                window.location.href = (await axios.get(this.$root.api(`slip/${submissionId}`))).data.url;
                setTimeout(() => {
                    this.downloadingSlip = false
                }, 5000);
            },
            async downloadMRC(submissionId) {
                this.downloadingMRC = true;
                window.location.href = (await axios.get(this.$root.api(`mrc/${submissionId}`))).data.url;
                setTimeout(() => {
                    this.downloadingMRC = false
                }, 5000);
            },
            editOffer() {
                this.$router.push(this.$route.path + '/edit');
            },
            async downloadDraft() {
                const firmOrder = this.submission.firm_orders.filter(fo => ['reviewing', 'captive_reviewing'].includes(fo.status))[0];
                const url = `/api/submission/${this.submission.id}/firm-order/download/${firmOrder.id}`;
                const fileUrl = (await axios.get(url)).data.url;

                window.location.href = fileUrl;
            },
            async downloadCaptiveDraft() {
                const firmOrder = this.submission.firm_orders.filter(fo => ['reviewing', 'captive_reviewing'].includes(fo.status))[0];
                const url = `/api/submission/${this.submission.id}/firm-order/captive-download/${firmOrder.id}`;
                const fileUrl = (await axios.get(url)).data.url;

                window.location.href = fileUrl;
            },
            async downloadReinsurerDraft() {
                const firmOrder = this.submission.firm_orders.filter(fo => ['reviewing', 'captive_reviewing'].includes(fo.status))[0];
                const url = `/api/submission/${this.submission.id}/firm-order/reinsurer-download/${firmOrder.id}`;
                const fileUrl = (await axios.get(url)).data.url;

                window.location.href = fileUrl;
            },
            async declineFirmOrder() {
                if (await this.$refs.decline.open() === false) {
                    return;
                }

                axios.put(this.$root.api(`insurer/submission/${this.submission.id}/firm-order/${this.myOfferedLine.offer_id}/decline`));

                this.$router.push('/insurer/dashboard');
            },
            initialiseUnderwriterReferences() {
                const references = {};
                this.myReviewingLines.forEach(line => {
                    references[line.id] = null;
                });
                this.underwriterReferences = references;
            },
            showSecurityName(line) {
                return this.myReviewingLines.length > 1 || Boolean(line.security);
            },
            securityName(line) {
                return line.security ? line.security.name : 'No security';
            },
            async acceptFirmOrder() {
                if (this.$refs.acceptForm.validate()) {
                    this.accepting = true;
                    this.acceptFirmOrderLoading = true;

                    await axios.put(this.$root.api(`insurer/submission/${this.submission.id}/firm-order/${this.myOfferedLine.offer_id}/accept`), {
                        references: this.myReviewingLines.map(line => ({
                            firm_order_offer_id: line.id,
                            underwriter_reference: this.underwriterReferences[line.id]
                        }))
                    }).then(() => {
                        this.$router.push({name: 'insurer-dashboard'});
                    }).catch(error => {
                        this.acceptFirmOrderLoading = false;
                    });
                }
            },
            async approveFirmOrder() {
                if (await this.$refs.approveFirmOrderModal.open() === false) {
                    return;
                }
                this.$eventBus.$off('action-approve-firm-order');
                this.pageLoading = true;
                await axios.put(this.$root.api(`insurer/submission/${this.submission.id}/firm-order/${this.myOfferedLine.offer_id}/captive-approve`)
                    ).then(() => {
                        this.$router.push({name: 'insurer-dashboard'});
                    }).catch(error => {
                        this.pageLoading = false;
                    });
            },
            allSubjectivitiesSelected(checkList) {
                const totalItems = checkList.length;
                const selectedItems = checkList.filter(s => s.selected === true).length;

                return totalItems === selectedItems || 'All subjectivities must be confirmed';
            },
            closeResponsePanel() {
                if (this.isEditingOffer) {
                    this.$router.push(this.$route.path.replace('/edit', ''));
                } else {
                    this.$emit('closeResponsePanel');
                }
            }
        },
        computed: {
            actionMenuItems() {
                if (this.myOfferIsReviewing) {
                    const actionItems = [];

                    if (!this.isStandalonePolicy) {
                        actionItems.push({
                            name: 'Draft declaration',
                            eventName: 'action-draft-declaration'
                        });
                    }

                    if (this.isCaptiveReinsurance && this.isFirmOrder) {
                        actionItems.push({
                            name: 'Draft captive declaration',
                            eventName: 'action-draft-captive-declaration'
                        });

                        actionItems.push({
                            name: 'Draft reinsurer declaration',
                            eventName: 'action-draft-reinsurer-declaration'
                        });
                    }

                    actionItems.push(
                        {
                            name: this.isCaptiveReinsurance ? 'Decline to reject firm order' : 'Decline',
                            eventName: 'action-decline-firm-order'
                        },
                        {
                            name: this.isCaptiveReinsurance ? 'Accept to approve firm order' : 'Accept',
                            eventName: 'action-accept-firm-order'
                        }
                    );

                    return actionItems
                }

                if (this.allOffersAccepted && this.$root.user.company.company_type === 'captive_insurer') {
                    return [
                        {
                            name: 'Approve',
                            eventName: 'action-approve-firm-order'
                        }
                    ]
                }
            },
            summaryTabs() {
                const tabs = [
                    {
                        value: 'product',
                        text: 'Product',
                    },
                ];

                if (this.submission.status !== 'ntu' && this.submission.information_sections.length > 0) {
                    tabs.push({
                        value: 'additional_information',
                        text: 'Additional Information',
                    })
                }

                tabs.push(
                    {
                        value: 'documents',
                        text: 'Documents',
                    },
                );

                return tabs;
            },
            isCaptiveReinsurance() {
                return this.submission.transaction_type === 'captive_reinsurance';
            },
            hasCaptiveInsurerOffer() {
                return Boolean(this.submission.offers.find(offer => Boolean(offer.captive_insurer_company_id)));
            },
            hasObligor() {
                return this.submission.hasOwnProperty('product') && !this.submission.product.has_no_obligor;
            },
            offer() {
                return this.submission ? this.submission.offers[0] : {};
            },
            has_responded () {
                return this.offer.status !== 'requested';
            },
            isOffered () {
                return this.offer.status === 'offered';
            },
            showSubjectivities () {
                return this.offer.subjectivities.length > 0
            },
            isFirmOrder() {
                return this.submission.hasOwnProperty('firm_orders') && this.submission.firm_orders !== null;
            },
            isEditingOffer () {
                return this.$route.path.endsWith('/edit');
            },
            insurerCanEdit () {
                return ['offered', 'declined'].includes(this.submission.status) && !this.isFirmOrder;
            },
            firmOrderLines() {
                if (this.isFirmOrder) {
                    // @TODO filter by user/company logged in
                    let firmOrder = this.submission.firm_orders.find(order => ['reviewing', 'captive_reviewing'].includes(order.status)) || {};
                    return firmOrder.firm_order_offers.filter(offer => ['firm_order', 'accepted', 'captive_reviewing'].includes(offer.status)) || {};
                }
            },
            myOfferedLines() {
                if (this.isFirmOrder) {
                    return this.firmOrderLines.filter(line => [line.insurer_company_id, line.captive_insurer_company_id].includes(this.$root.user.company_id));
                }
                return [];
            },
            myOfferedLine() {
                if (this.isFirmOrder) {
                    return this.myOfferedLines[0] || {};
                }
            },
            myReviewingLines() {
                return this.myOfferedLines.filter(line => line.status === 'firm_order');
            },
            myTotalWrittenLineAmount() {
                return this.myOfferedLines.reduce((sum, line) => sum + (line.written_line_amount || 0), 0);
            },
            myOfferIsReviewing() {
                if (this.isFirmOrder) {
                    return this.myOfferedLines.some(line => line.status === 'firm_order');
                }
            },
            allOffersAccepted() {
                if (this.firmOrderLines) {
                    const acceptedFirmOrderLines = this.firmOrderLines.filter(offer => offer.status === 'accepted');
                    return acceptedFirmOrderLines.length === this.firmOrderLines.length;
                }
            },
            showReviewOfferBanner() {
                if (this.submission.offers[0].submission_updated && (this.isOffered || this.isFirmOrder)) {
                    return true;
                }
                return false;
            },
            showReviewPolicyWordingAndMRCBanner() {
                return this.isStandalonePolicy && (this.isOffered || this.isFirmOrder);
            },
            showSubmissionResponse() {
                return !this.has_responded || this.isEditingOffer;
            },
        },
        watch: {
            actionMenuItems: {
                handler(newItems) {
                    this.$eventBus.$emit('update-action-menu-items', newItems);
                },
                immediate: true
            }
        }
    }
</script>

<style lang="scss" scoped>
    .container.fluid.response-active {
        max-width: 50%;
        margin: 0;
        .layout {
            flex-wrap: wrap;
        }
        .xs6 {
            flex-basis: 100%;
            flex-grow: 0;
            max-width: 100%;
        }
    }

    .subjectivities-list {
        padding-left: 0.8rem;
        line-height: 1.5em;
    }

    .security-name {
        margin-bottom: 8px;
        overflow-wrap: break-word;
        word-break: break-word;
    }
</style>
